// Home.tsx (FULL DROP-IN)
// Fix: React.lazy only code-splits; it does NOT "load on scroll" unless you gate rendering.
// This version gates each below-the-fold section with LazySection (IntersectionObserver),
// so Lighthouse/network won't download/render everything at once.

import React, { useMemo, useState, useEffect, Suspense } from "react"
import { motion, AnimatePresence } from "framer-motion"
import Header from "../components/Header"
import RotatingPhrase from "../components/RotatingPhrase"
import LeafIcon from "../components/icons/LeafIcon"
import LazySection from "../components/LazySection"
import { useLocation, useNavigate } from "react-router-dom"
import { useJitsu } from "@jitsu/jitsu-react"
import { getOrCreateSessionId } from "../lib/session"

// ✅ Lazy components (code-split)
const FeatureSliderHero = React.lazy(() => import("../components/FeatureSliderHero"))
const CategoryCardsSection = React.lazy(() => import("../components/CategoryCardsSection"))
const DiseasesSlider = React.lazy(() => import("../components/DiseasesSlider"))
const ScienceAndPatientsSection = React.lazy(() => import("../components/ScienceAndPatientsSection"))
const StoriesVideoSection = React.lazy(() => import("../components/StoriesVideoSection"))
const PricingPlansSection = React.lazy(() => import("../components/PricingPlansSection"))
const CallToAction = React.lazy(() => import("../components/CallToAction"))
const ImageMosaicGrid = React.lazy(() => import("../components/ImageMosaicGrid"))
const FaqSection = React.lazy(() => import("../components/FaqSection"))
const Footer = React.lazy(() => import("../components/Footer"))

// ---------- JSON-LD helpers ----------
function JsonLd({ data }: { data: unknown }) {
  return <script type="application/ld+json">{JSON.stringify(data)}</script>
}

// Strips HTML tags if you ever add markup to the strings.
// Keeps JSON-LD clean and prevents broken structured data.
function stripHtml(input: string) {
  return input.replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim()
}

// Shared skeleton
const SectionSkeleton = (
  <div className="mx-4 my-6 rounded-2xl bg-black/5 p-6">
    <div className="h-5 w-48 rounded bg-black/10" />
    <div className="mt-3 h-4 w-72 rounded bg-black/10" />
    <div className="mt-3 h-40 w-full rounded bg-black/10" />
  </div>
)

export default function Home() {
  const { analytics } = useJitsu()

  const phrases = useMemo(
    () => ["desestressar", "performance", "aliviar dores", "dormir melhor", "ansiedade", "perder peso"],
    []
  )

  const bgByPhrase: Record<string, string> = useMemo(
    () => ({
      "desestressar": "/bg-estresse.webp",
      performance: "/bg-performance.webp",
      "aliviar dores": "/bg-dores.webp",
      "dormir melhor": "/bg-insonia.webp",
      ansiedade: "/bg-ansiedade.webp",
      "perder peso": "/bg-obesidade.webp",
    }),
    []
  )

  const bgByPhraseMobile: Record<string, string> = useMemo(
    () => ({
      "desestressar": "/bg-estresse-mobile.webp",
      performance: "/bg-performance-mobile.webp",
      "aliviar dores": "/bg-dores-mobile.webp",
      "dormir melhor": "/bg-insonia-mobile.webp",
      ansiedade: "/bg-ansiedade-mobile.webp",
      "perder peso": "/bg-obesidade-mobile.webp",
    }),
    []
  )

  const mobileWidthByPhrase: Record<string, string> = useMemo(
    () => ({
      "desestressar": "w-[35%]",
      performance: "w-[35%]",
      "aliviar dores": "w-[35%]",
      "dormir melhor": "w-[45%]",
      ansiedade: "w-[35%]",
      "perder peso": "w-[35%]",
    }),
    []
  )

  const [activeIndex, setActiveIndex] = useState(0)
  const activePhrase = phrases[activeIndex] ?? phrases[0]
  const activeBg = bgByPhrase[activePhrase] ?? "/bg-default.webp"
  const activeBgMobile = bgByPhraseMobile[activePhrase] ?? "/bg-default-mobile.webp"

  const [committedPhrase, setCommittedPhrase] = useState(phrases[0])
  const activeMobileWidth = mobileWidthByPhrase[committedPhrase] ?? "w-[35%]"

  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    const targetId = location.state?.scrollToId
    if (!targetId) return

    let attempts = 0
    const maxAttempts = 40

    const tryScroll = () => {
      const el = document.getElementById(targetId)

      if (el) {
        const headerOffset = window.innerWidth < 480 ? 34 : 50
        const y = el.getBoundingClientRect().top + window.scrollY - headerOffset - 12

        window.scrollTo({
          top: y,
          behavior: "smooth",
        })

        // clears state so refresh/back doesn't keep scrolling again
        navigate(location.pathname, { replace: true, state: {} })
        return
      }

      attempts += 1
      if (attempts < maxAttempts) {
        requestAnimationFrame(tryScroll)
      }
    }

    tryScroll()
  }, [location.state, location.pathname, navigate])

  // Prefetch next bg to avoid flicker when phrase changes
  useEffect(() => {
    const nextPhrase = phrases[(activeIndex + 1) % phrases.length]
    if (!nextPhrase) return

    const isMobile = window.matchMedia("(max-width: 1024px)").matches

    const nextBg = isMobile
      ? bgByPhraseMobile[nextPhrase]
      : bgByPhrase[nextPhrase]

    if (!nextBg) return

    const img = new Image()
    img.src = nextBg
  }, [activeIndex, phrases, bgByPhrase, bgByPhraseMobile])

  // ✅ Keep FAQ data in ONE place: used by UI and by JSON-LD.
  const faqItems = useMemo(
    () => [
      {
        question: "Só consigo comprar com prescrição médica?",
        answer:
          "Sim. De acordo com a ANVISA, todos devem ter a prescrição médica para importar os produtos à base de cannabis. Por isso, você pode agendar uma consulta com um de nossos prescritores, ou apresentar sua receita caso já seja acompanhado(a) por outro profissional.",
      },
      {
        question: "Como funciona a consulta online?",
        answer:
          "É bem simples! Temos alguns médicos de plantão. Por isso, não precisa agendar! A consulta é feita imediatamente na plataforma através de chat, após o cadastro, e dura entre 15 e 30min. Após a consulta, o médico já envia a sua prescrição, se o tratamento for elegível para o seu caso. Se você tiver interesse em consultas por vídeo, só entrar em contato conosco para agendar.",
      },
      {
        question: "Este tratamento é permitido no Brasil?",
        answer:
          "Sim. Trabalhamos conforme a RDC 660/22, regulamentação da ANVISA, que permite a importação de produtos à base de cannabis para pessoas físicas, com fins medicinais, sob prescrição médica.",
      },
      {
        question: "Como conseguir a autorização da Anvisa para importar os produtos?",
        answer:
          "Já com a receita, te ajudaremos através da plataforma a emitir a autorização da Anvisa (documento obrigatório) para garantir o direito de usar e transportar os produtos em todo Brasil. É um processo rápido, em poucos minutos, feito de forma online.",
      },
      {
        question: "De onde vem os produtos?",
        answer:
          "Todos os produtos são importados dos EUA diretamente para a casa dos pacientes. Facilitamos o seu tratamento, fazendo uma curadoria de marcas já conceituadas no mercado, que apresentam testes de qualidade e bons resultados aos tratamentos.",
      },
      {
        question: "Qual é o prazo de entrega?",
        answer: "O prazo de entrega é de até 20 dias úteis, após aprovação da documentação.",
      },
      {
        question: "Quanto tempo dura o frasco?",
        answer: "Como o tratamento é individual, o tempo de duração do frasco varia conforme a dose diária.",
      },
      {
        question: "Posso viajar com os produtos?",
        answer: "Dentro do Brasil, você pode portar normalmente com receita e autorização da Anvisa.",
      },
      {
        question: "Qual a política de devolução dos produtos?",
        answer:
          "Após a saída dos produtos do aeroporto de origem (EUA), não é possível cancelar/desistir da compra. A troca ou devolução só é permitida caso o produto apresente defeitos de fabricação, danos no transporte ou outras irregularidades que a justifiquem.",
      },
    ],
    []
  )

  // ✅ FAQPage structured data (JSON-LD)
  const faqJsonLd = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqItems.map((it) => ({
        "@type": "Question",
        name: stripHtml(it.question),
        acceptedAnswer: {
          "@type": "Answer",
          text: stripHtml(it.answer),
        },
      })),
    }),
    [faqItems]
  )

  return (
    <div id="home" className="min-h-screen bg-white">
      <Header />

      {/* ✅ JSON-LD goes anywhere in the DOM; browsers will still parse it.
          Putting it near the top keeps it deterministic. */}
      <JsonLd data={faqJsonLd} />

      <main className="pt-11">
        {/* HERO (eager) */}
        <section
          id="banner"
          className="
            relative
            mx-auto max-w-[100%] xl:max-w-[90%]
            h-[240px] sm:h-[440px] md:h-[520px] lg:h-[520px] xl:h-[520px] 2xl:h-[520px] 
            px-4 lg:px-8 xl:px-4 sm:pb-12 sm:pt-22
          "
        >
          <div className={`z-49 banner-auto-width absolute right-0 mt-0 -top-3 h-[calc(100%+2.75rem+1rem)] ${activeMobileWidth} lg:top-0 lg:h-full lg:w-[45%] xl:w-[45%] overflow-hidden`}>
            <AnimatePresence mode="wait" onExitComplete={() => setCommittedPhrase(activePhrase)}>
              <motion.picture
                key={activeBg}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
                className="absolute inset-0 h-full w-full"
              >
                <source media="(max-width: 1024px)" srcSet={activeBgMobile} />
                <motion.img
                  src={activeBg}
                  alt=""
                  aria-hidden="true"
                  className="absolute inset-0 h-full w-full object-cover object-top"
                  decoding="async"
                  fetchPriority={activeIndex === 0 ? "high" : "low"}
                  loading={activeIndex === 0 ? "eager" : "lazy"}
                />
              </motion.picture>
            </AnimatePresence>
          </div>
          <div className="relative z-60 h-full lg:h-auto content-center">
            <LeafIcon className="text-primary-darker h-[14px] w-[14px] lg:w-[24px] mb-3 sm:mb-5" />



            <h1 className="text-[20px] sm:text-[37px] md:text-[40px]! leading-[0.7] font-display text-black">
              Planos personalizados para{" "}
              <span className="leading-none z-50 font-semibold block text-[48px] sm:text-[80px] md:text-[90px] lg:text-[110px] text-primary-darker">
                <RotatingPhrase phrases={phrases} intervalMs={2600} onIndexChange={setActiveIndex} />
              </span>
              <span className="block text-[21.5px] sm:text-[40px] md:text-[44px] leading-[0.7]">tudo baseado em ciência</span>
            </h1>
            <div className="mb-3 mt-2 sm:mt-8 pl-[2px] sm:mb-10 flex items-center gap-3">
              <p className="font-sans text-[7px] sm:text-[9px] md:text-[12px] tracking-[0.2rem] sm:tracking-[0.5rem] text-black">CONSULTAS NA HORA COM<br className="sm:hidden" /> MÉDICOS ESPECIALISTAS</p>
            </div>
            <div className="mt-5 sm:mt-10">
              <a
                href="https://app.nativamed.com.br"
                target="_blank"
                rel="noreferrer"
                className="font-sans font-bold inline-flex h-8 sm:h-12 items-center justify-center rounded-full bg-primary-darker px-3 sm:px-5 text-[10px] sm:text-lg text-white shadow-lg transition hover:bg-primary"
                onClick={() => analytics.track("external_link_click", {
                  session_id: getOrCreateSessionId(),
                  label: "começar_avaliação_hero",
                })}
              >
                Começar avaliação
              </a>
            </div>
          </div>
        </section>

        <section id="diseases" className="lg:mx-4 pb-10 z-51">
          <Suspense fallback={SectionSkeleton}>
            <DiseasesSlider
              options={[
                "Ansiedade",
                "Estresse",
                "Dores",
                "Foco",
                "Sono",
                "Performance",
                "Emagrecer",
                "Libido",
                "Enxaqueca",
                "Depressão",
                "Menopausa",
                "Neurológico",
              ]}
              onSelect={(motivo) =>
                window.open(
                  `https://app.nativamed.com.br/?motivo=${encodeURIComponent(motivo)}`,
                  "_blank",
                  "noreferrer",
                )
              }
            />
          </Suspense>
        </section>

        {/* PHONE (you can keep eager OR gate it as well; leaving eager here) */}
        <section id="phone" className="pb-10 xl:mx-4 lg:rounded-4xl rounded-2xl z-60 relative">
          <Suspense fallback={SectionSkeleton}>
            <FeatureSliderHero
              phoneImageSrc="/phone-in-hand.webp"
              ctaHref="https://app.nativamed.com.br"
              ctaLabel="Iniciar agora"
              features={[
                {
                  title: "Consulta médica",
                  body: (
                    <>
                      <b>100% online e sem agendamento</b>
                      <br />
                      É só iniciar agora e nossa equipe de médicos capacitados está pronta para atender você, por chat ou vídeo.
                    </>
                  ),
                },
                {
                  title: "Documentação",
                  body: "Após a consulta, você irá receber em sequência a receita médica. Seguiremos com a sua autorização da Anvisa de modo automático direto na nossa plataforma.",
                },
                {
                  title: "Compra do produto",
                  body: "Com a documentação pronta, você já pode realizar a compra do produto na plataforma da nativamed.",
                },
                {
                  title: "Entrega segura",
                  body: "Seu tratamento chega até você, com praticidade e suporte contínuo.",
                },
              ]} screenImageSrc={"phone-screen-1.webp"} screenImageSrc2={"phone-screen-2.webp"}       />
          </Suspense>
        </section>

        {/* ✅ BELOW THE FOLD: truly load-on-scroll (gated render) */}
        <LazySection rootMargin="800px 0px" fallback={SectionSkeleton} minHeight={420}>
          <section id="products" className="rounded-2xl py-10">
            <Suspense fallback={SectionSkeleton}>
              <CategoryCardsSection
                items={[
                  {
                    title: "Cannabis medicinal",
                    description: "Tratamentos personalizados com base científica para ansiedade, sono e outros",
                    href: "/cannabis-medicinal",
                    mode: "accordion",
                    ctaLabel: "Saiba mais",
                    imageSrc: "/img-cannabis.webp",
                    imageAlt: "Frasco conta-gotas",
                    imageClassName: "!w-[27%] lg:!w-[20%] xl:!w-[27%] top-0 -translate-y-[24px]",
                    accordionContent: (
                      <div className="space-y-6">
                        {/* O que é a cannabis */}
                        <div className="space-y-3">
                          <h3 className="font-sans text-[16px] text-black">O que é a cannabis?</h3>
                          <p className="font-sans text-[11px] sm:text-[13px] leading-snug text-black">
                            A cannabis medicinal é o uso da <strong>planta <em>Cannabis sativa</em></strong> como tratamento de saúde, com <strong>segurança e eficácia comprovadas cientificamente.</strong><br/>
                            Contém substâncias chamadas fitocanabinoides, como o CBD e o THC, que interagem com o nosso organismo de formas surpreendentes e estão sendo cada vez mais estudadas.
                          </p>
                        </div>

                        {/* Como você vai usar no dia a dia */}
                        <div className="space-y-3">
                          <h3 className="font-sans text-[16px] text-black">Como você vai usar no dia a dia</h3>
                          <p className="font-sans text-[11px] sm:text-[13px] leading-snug text-black">
                            Escolhemos as formas mais práticas para facilitar o seu tratamento:
                          </p>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                            {[
                              {
                                label: "Óleo",
                                img: "/img-oleo.webp",
                                description: "Gotinhas embaixo da língua: absorção rápida e fácil de ajustar a dose conforme o tratamento evolui.",
                              },
                              {
                                label: "Gummies",
                                img: "/img-gummies.webp",
                                description: "Balinhas de goma! Gostosas, discretas e perfeitas para quem não gosta de óleos ou tem dificuldade com o sabor.",
                              },
                              {
                                label: "Gel massageador",
                                img: "/img-gel.webp",
                                description: "Passa direto no local da dor, com roll-on massageador. Age onde você precisa, de forma rápida e ainda com efeito gelado.",
                              },
                            ].map(({ label, img, description }) => (
                              <div
                                key={label}
                                className="flex flex-row items-stretch bg-[#f7faf7] overflow-hidden h-28 sm:h-32"
                                style={{ borderTopLeftRadius: '200rem', borderBottomLeftRadius: '200rem', borderTopRightRadius: '9999px', borderBottomRightRadius: '9999px' }}
                              >
                                <div className="p-5 sm:p-6 flex-1 flex flex-col justify-center gap-1 min-w-0 overflow-hidden">
                                  <span className="font-sans text-xs sm:text-sm font-medium text-black">{label}</span>
                                  <p className="font-sans text-[10px] sm:text-xs leading-snug text-black/60">{description}</p>
                                </div>
                                <div className="shrink-0 h-full aspect-square">
                                  <img
                                    src={img}
                                    alt={label}
                                    className="rounded-full h-full w-full object-cover"
                                    loading="lazy"
                                    decoding="async"
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Seu protocolo é único */}
                        <div className="space-y-3">
                          <h3 className="font-sans text-[16px] text-black">Seu protocolo é único</h3>
                          <p className="font-sans text-[10px] sm:text-xs leading-snug text-black">
                            <strong>Mas atenção: nem todo produto de cannabis é igual!</strong> A composição varia muito
                            de uma formulação para outra, e é exatamente essa combinação de canabinoides que define o
                            efeito de cada tratamento. Por isso, nada de receita genérica:{" "}
                            <strong>o protocolo é montado especialmente para você, por um médico capacitado</strong>,
                            levando em conta o seu histórico, suas queixas e o que faz mais sentido para o seu corpo.
                          </p>
                        </div>

                        {/* Canabinoides */}
                        <div className="shrink-0 sm:w-1/2 justify-self-center">
                          <img
                            src="canabinoides.webp"
                            alt="Canabinoides"
                            className="h-full w-full object-cover"
                            loading="lazy"
                            decoding="async"
                          />
                        </div>
                      </div>
                    ),
                  },
                  {
                    title: "Suplementos",
                    description: "Suporte para energia, foco e equilíbrio no dia a dia",
                    href: "/",
                    mode: "coming-soon",
                    comingSoonText: "EM BREVE",
                    comingSoonDurationMs: 2500,
                    imageSrc: "/img-suplementos.webp",
                    imageAlt: "Pote de suplemento",
                    imageClassName: "top-1/2 -translate-y-1/2",
                  },
                  {
                    title: "Perda de peso",
                    description: "Acompanhamento médico para resultados sustentáveis com canetas",
                    href: "/",
                    mode: "coming-soon",
                    comingSoonText: "EM BREVE",
                    comingSoonDurationMs: 2500,
                    imageSrc: "/img-perda-peso.webp",
                    imageAlt: "Canetas para perda de peso",
                    imageClassName: "top-1/2 -translate-y-1/2",
                  },
                ]}
              />
            </Suspense>
          </section>
        </LazySection>

        <LazySection rootMargin="900px 0px" fallback={SectionSkeleton} minHeight={520}>
          <section id="science-reviews">
            <Suspense fallback={SectionSkeleton}>
              <ScienceAndPatientsSection
                title="O que a ciência comprova ... nossos pacientes confirmam"
                science={[
                  { title: "Cannabis medicinal no tratamento de ansiedade", image: "/science1.webp", href: "https://pubmed.ncbi.nlm.nih.gov/40413923/" },
                  { title: "Eficácia da cannabis na dor", image: "/science2.webp", href: "https://pubmed.ncbi.nlm.nih.gov/28934780/" },
                  { title: "Cannabis para insônia", image: "/science3.webp", href: "https://pubmed.ncbi.nlm.nih.gov/34115851/" },
                  { title: "Cannabis e TDAH", image: "/science4.webp", href: "https://pmc.ncbi.nlm.nih.gov/articles/PMC8832253/" },
                ]}
                testimonials={[
                  {
                    title: "Allan Demarch",
                    description:
                      "O atendimento foi excelente, tudo fácil e simples. Superou minhas expectativas, pois achei que seria algo complicado e burocrático. Já estou com o medicamento e está fazendo a diferença na minha vida. Recomendo fortemente!!",
                    href: "https://maps.app.goo.gl/qedErCYXoGNkWmR4A",
                  },
                  {
                    title: "Daniela Kandini",
                    description:
                      "A nativamed é muito acessível, prática e fácil de usar. Desde o início, fui atendida por médicos extremamente atenciosos, humanos e profissionais. Sofria há muitos anos com insônia e, em muitas noites, simplesmente não conseguia dormir, mesmo fazendo uso de medicamentos controlados. Após iniciar o tratamento com cannabis, minha qualidade de vida melhorou significativamente. Hoje consigo dormir a noite toda, algo que não acontecia há muitos anos.",
                    href: "https://maps.app.goo.gl/KL8ua587YNiMHvGa6",
                  },
                  {
                    title: "Brenda Flores",
                    description:
                      "Fui muito bem atendida, desde a médica na consulta até a equipe que tirou minhas dúvidas depois que eu já estava tomando. Continuaram me dando suporte. A cannabis está me ajudando demais. Comecei a tomar para insônia, mas sinto que até as dores musculares que eu tinha às vezes melhoraram muito.",
                    href: "https://maps.app.goo.gl/ZXeEar2K4W9QW1jg6",
                  },
                  {
                    title: "Deni Didomenico",
                    description:
                      "Fui muito bem atendida e hoje sou outra pessoa, com a redução das minhas dores que me acompanhavam em anos e hoje estou livre. Fibromialgia não tem cura mas a cannabis reduziu muito minhas dores.",
                    href: "https://maps.app.goo.gl/kzCU12HVXJB1SKePA",
                  },
                  {
                    title: "Tayane Tovo Kittlaus",
                    description:
                      "Tenho uma rotina muito intensa de estudo, e sempre fico muito ansiosa antes das provas. Comecei o tratamento para dormir com medicamentos tradicionais, mas sentia muita tontura, dor de cabeça e não sentia que o corpo descansava de verdade. Depois que conheci o CBD me sinto bem melhor, consigo pensar com calma e me manter mais focada nas aulas. Realmente, fez toda diferença na minha rotina. Não fico mais sem!",
                    href: "https://maps.app.goo.gl/XECj6oJBLmryPgZq6",
                  },
                  {
                    title: "Nathalia Guizardi",
                    description:
                      "Excelente! Plataforma muito intuitiva, todo o processo foi muito rápido, já fui atendida pelo médico na hora mesmo e comprei em seguida. Busquei a cannabis pra me ajudar a lidar com a ansiedade, comecei a tomar essa semana, 2 gotinhas só, e já to sentindo diferença, to mais centrada e meu sono melhorou, to acordando mais descansada já",
                    href: "https://maps.app.goo.gl/u8doRPcQKibdT7nP9",
                  },
                  {
                    title: "Denise Santana",
                    description:
                      "Muito bom o atendimento da nativamed, foi bem rápido e a Caroline me ajudou em todos os processos com muita gentileza. To usando o óleo pra controlar a ansiedade e tá me ajudando muito!! To muito feliz em ter encontrado eles pq todo o atendimento desde a consulta até a compra foi todo online e não precisei me deslocar.",
                    href: "https://maps.app.goo.gl/jsxGBtiapdG5vTEu5",
                  },
                  {
                    title: "Natália Flores",
                    description:
                      "Tenho bruxismo e comecei usar as gomas por indicação de uma amiga pq sentia muitas dores no maxilar. To comendo as gomas antes de dormir, e tem me ajudado muito a relaxar, dormir melhor e acordar sem dor. Recomendo muito para quem também sofre com isso.",
                    href: "https://maps.app.goo.gl/5dGpYrTyAG67BwHEA",
                  },
                ]}
              />
            </Suspense>
          </section>
        </LazySection>

        <section id="videos" className="py-10 lg:py-6">
          <LazySection rootMargin="900px 0px" fallback={SectionSkeleton} minHeight={420}>
            <Suspense fallback={SectionSkeleton}>
              <StoriesVideoSection
                items={[
                  { name: <>Carol | <b>Ansiedade</b></>, nameLabel: "Carol - Ansiedade", posterSrc: "/carol.webp", videoSrc: "/videos/NATIVAMED-caroline.mp4" },
                  { name: <>Yuri | <b>Performance</b></>, nameLabel: "Yuri - Performance", posterSrc: "/yuri.webp", videoSrc: "/videos/NATIVAMED-yuri.mp4" },
                  { name: <>Maria | <b>Dor Crônica</b></>, nameLabel: "Maria - Dor Crônica", posterSrc: "/silvana.webp", videoSrc: "https://www.youtube.com/embed/VIDEO_ID?autoplay=1&rel=0" },
                ]}
              />
            </Suspense>
          </LazySection>
        </section>

        <LazySection rootMargin="900px 0px" fallback={SectionSkeleton} minHeight={420}>
          <section id="pricing-plans" className="py-10 lg:py-6">
            <Suspense fallback={SectionSkeleton}>
              <PricingPlansSection />
            </Suspense>
          </section>
        </LazySection>

        <LazySection rootMargin="900px 0px" fallback={SectionSkeleton} minHeight={320}>
          <section id="cta" className="mx-4 pb-6 pt-10">
            <Suspense fallback={SectionSkeleton}>
              <CallToAction buttonLabel="Iniciar agora" href="#protocolo" />
            </Suspense>
          </section>
        </LazySection>

        <section id="galeria" className="pt-6 pb-10">
          <LazySection rootMargin="900px 0px" fallback={SectionSkeleton} minHeight={520}>
            <Suspense fallback={SectionSkeleton}>
              <ImageMosaicGrid />
            </Suspense>
          </LazySection>
        </section>

        {/* ✅ FAQ (uses same faqItems array as JSON-LD) */}
        <section id="faq" className="py-10">
          <LazySection rootMargin="900px 0px" fallback={SectionSkeleton} minHeight={520}>
            <Suspense fallback={SectionSkeleton}>
              <FaqSection title="Dúvidas e respostas" items={faqItems} />
            </Suspense>
          </LazySection>
        </section>

        <LazySection rootMargin="1200px 0px" fallback={null} minHeight={260}>
          <Suspense fallback={null}>
            <Footer />
          </Suspense>
        </LazySection>
      </main>
    </div>
  )
}